import { EventEmitter, inject, Injectable } from '@angular/core';
import { environment } from '@src/environments/environment';
import { ContainerModel } from '@app/models/server/layout.model';
import { OrderResponseModel } from '@app/models/client/order-request.model';
import { AnalyticsAdapter } from '@app/api-v2/util/analytics/analytics.adapter';

const WEB_BOOKING_CONFIRMATION: string = String('booking_confirmation');
const APP_BOOKING_CONFIRMATION: string = String('app_bookings');

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  fireAnalyticsEvent: EventEmitter<{ eventName: string; data: any }> =
    new EventEmitter<any>();

  fireCartContainerEvent: EventEmitter<{
    eventName: string;
    cartContainer: ContainerModel[];
  }> = new EventEmitter<any>();

  fireBookingConfirmationEvent: EventEmitter<{
    orderResponse: OrderResponseModel;
  }> = new EventEmitter<any>();

  private analyticsAdapter: AnalyticsAdapter;

  constructor() {
    this.analyticsAdapter = inject(AnalyticsAdapter);

    this.fireAnalyticsEvent.subscribe({
      next: (result: { eventName: string; data: any }) => {
        this.fireEvent(result?.eventName, result?.data);
      },
    });

    this.fireCartContainerEvent.subscribe({
      next: (result: {
        eventName: string;
        cartContainer: ContainerModel[];
      }) => {
        this.cartContainerEvent(result?.eventName, result?.cartContainer);
      },
    });

    this.fireBookingConfirmationEvent.subscribe({
      next: (result: { orderResponse: OrderResponseModel }) => {
        this.fireBookingSuccessEvent(result?.orderResponse);
      },
    });
  }

  setScreenName(url: string) {
    this.analyticsAdapter?.setScreenName(url);
  }

  fireSearchEvent(featureType: string, pageType: string, data: any) {
    let eventName: string = String('search');
    if (featureType) {
      eventName = eventName + '_' + featureType.toLowerCase();
    }
    if (pageType) {
      eventName = eventName + '_' + pageType.toLowerCase();
    }
    this.analyticsAdapter?.fireEvent(eventName, data);
  }

  fireTextSearch(value: string, featureType?: string) {
    let eventName: string = String('text_search');
    if (featureType) {
      eventName = eventName + '_' + featureType.toLowerCase();
    }
    this.analyticsAdapter?.fireEvent(eventName, value);
  }

  private fireEvent(eventName: string, data: any) {
    this.analyticsAdapter?.fireEvent(eventName, data);
  }

  private fireBookingSuccessEvent(data: OrderResponseModel) {
    if (environment?.webApp) {
      this.analyticsAdapter?.fireBookingEvent(WEB_BOOKING_CONFIRMATION, data);
    } else {
      this.analyticsAdapter?.fireBookingEvent(APP_BOOKING_CONFIRMATION, data);
    }
  }

  cartContainerEvent(eventName: string, cartContainers: ContainerModel[]) {
    let type: string;
    const data: { name: string }[] = [];

    for (const cartContainer of cartContainers) {
      type = cartContainer?.feature?.type;
      if (cartContainer?.data?.length > 0) {
        for (const containerItem of cartContainer?.data) {
          data.push({ name: containerItem?.title });
        }
      }
      if (cartContainer?.containers?.length > 0) {
        for (const containerItem of cartContainer?.containers) {
          data.push({ name: containerItem?.title });
        }
      }
    }

    if (type) {
      eventName = eventName + '_' + type.toLowerCase();
    }
    this.analyticsAdapter?.fireEvent(eventName, data);
  }

  registrationEvent(result: { data: any; platform: string }) {
    if (environment?.webApp) {
      this.fireEvent('registration_web', result?.data);
    } else {
      if (result?.platform === 'android') {
        this.fireEvent('registration_android', result?.data);
      } else {
        this.fireEvent('registration_ios', result?.data);
      }
    }
  }

  setUserId(_id: string) {
    this.analyticsAdapter?.setUserId(_id);
  }
}
